'use client';

import { Transition } from '@headlessui/react';
import { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { cx } from 'react-twc';

import { EmailVerifyError, useVerifyCode, useVerifyEmail } from '@/api-hooks/useEmailVerify';
import ContainedButton from '@/components/Button/ContainedButton';
import OutlinedButton from '@/components/Button/OutlinedButton';
import TonalButton from '@/components/Button/TonalButton';
import Input from '@/components/Input';
import useTimer from '@/hooks/useTimer';
import BlueCheckIcon from '@/icons/BlueCheckIcon';
import CircleInfo from '@/icons/CircleInfo';
import ErrorCircle from '@/icons/ErrorCircle';
import { formatTimemmss } from '@/utils/format';
import { removeSpace } from '@/utils/parse-input';
import { isEmail } from '@/utils/validation';

type EmailVerifyStatus = 'not-requested' | 'requested' | 'verified';

const EmailVerify = ({
  email,
  setEmail,
  code,
  setCode,
}: {
  email: string;
  setEmail: (email: string) => void;
  code?: number;
  setCode: (code?: number) => void;
}) => {
  const { seconds, resetTimer } = useTimer(300);
  const [status, setStatus] = useState<EmailVerifyStatus>('not-requested');

  const { mutate: sendVerifyEmail, isPending: isSending } = useVerifyEmail('reset');
  const { mutate: submitVerifyCode, isPending: isVerifying } = useVerifyCode();

  const [emailErrorMessage, setEmailErrorMessage] = useState<string | null>(
    '이메일 형식을 확인해주세요',
  );
  const [isEmailErrored, setIsEmailErrored] = useState(false);

  const disabledRequestVeiryButton = !email || !isEmail(email);

  const handleChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    const removedSpaceEmail = removeSpace(e.target.value);
    setEmail(removedSpaceEmail);
  };

  const handleChangeCode = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length > 6) return;
    setCode(e.target.value === '' ? undefined : Number(e.target.value));
  };

  const handleClickSendEmail = () => {
    if (!isEmail(email)) {
      toast.error('이메일 형식을 확인해주세요');
      return;
    }

    if (!email) {
      toast.error('이메일을 입력해주세요');
      return;
    }

    sendVerifyEmail(email, {
      onSuccess: () => {
        resetTimer();
        setStatus('requested');
        setIsEmailErrored(false);
      },
      onError: (error) => {
        if (error instanceof EmailVerifyError && error.status === 404) {
          toast.error('가입하지 않는 이메일입니다');
          setEmailErrorMessage('가입하지 않는 이메일입니다');
          return;
        }
        setStatus('not-requested');
        toast.error(error.message || '인증번호 발급에 실패했습니다');
      },
    });
  };

  const handleResendEmail = () => {
    setStatus('not-requested');
    handleClickSendEmail();
  };

  const handleSumitCode = () => {
    if (!code) {
      return toast.error('인증번호를 입력해주세요');
    }

    submitVerifyCode(
      { email, code: String(code) },
      {
        onSuccess: () => {
          setStatus('verified');
        },
        onError: (error) => {
          if (error instanceof EmailVerifyError && error.status === 401) {
            toast.error('인증번호가 일치하지 않습니다');
            return;
          }
          toast.error(error.message || '인증번호가 일치하지 않습니다');
        },
      },
    );
  };

  const handleKeyDownEnterEmail = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if (disabledRequestVeiryButton || isSending) return;
      handleClickSendEmail();
    }
  };

  const handleKeyDownEnterCode = (event: KeyboardEvent<HTMLInputElement>) => {
    if (['e', 'E', '-', '+', '.'].includes(event.key)) {
      event.preventDefault();
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if (isVerifying) return;
      handleSumitCode();
    }
  };

  const handleBlurEmail = () => {
    if (!email) return;
    if (!isEmail(email)) {
      setIsEmailErrored(true);
    } else {
      setIsEmailErrored(false);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-2.5 mt-12">
        <label htmlFor="email" className="font-bold flex gap-2 items-center">
          아이디
          {status === 'verified' && (
            <span className="text-secondary inline-flex gap-0.5 text-xs items-center">
              <BlueCheckIcon className="size-4" />
              인증되었습니다.
            </span>
          )}
        </label>
        <div className="flex gap-2.5">
          <Input
            type="email"
            placeholder="example@example.com"
            className={cx('flex-1', isEmailErrored && 'ring-2 ring-error')}
            id="emailInput"
            onChange={handleChangeEmail}
            value={email}
            onKeyDown={handleKeyDownEnterEmail}
            onBlur={handleBlurEmail}
            disabled={status === 'verified' || status === 'requested'}
          />
          {status === 'requested' ? (
            <TonalButton
              className="w-[124px]"
              color="primary"
              type="button"
              onClick={handleResendEmail}
              disabled={isSending}
            >
              인증번호 재발급
            </TonalButton>
          ) : status === 'not-requested' ? (
            <>
              <ContainedButton
                className={cx('w-[124px] transition-all')}
                color={'primary'}
                onClick={handleClickSendEmail}
                type="button"
                disabled={disabledRequestVeiryButton || isSending}
              >
                인증번호 받기
              </ContainedButton>
            </>
          ) : (
            <TonalButton className="w-[124px]" color="primary" disabled type="button">
              인증완료
            </TonalButton>
          )}
        </div>
        {!isEmailErrored && status === 'requested' && (
          <p className="text-secondary text-xs font-medium inline-flex items-center gap-2">
            <CircleInfo className="text-secondary" />
            메일이 발송되었습니다
          </p>
        )}
        {isEmailErrored && (
          <p className="text-error text-xs font-medium inline-flex items-center gap-2">
            <ErrorCircle />
            {emailErrorMessage}
          </p>
        )}
      </div>
      {seconds === 0 && (
        <TimerEnd
          setStatus={setStatus}
          status={status}
          setEmailErrorMessage={setEmailErrorMessage}
          setIsEmailErrored={setIsEmailErrored}
        />
      )}

      <Transition
        show={status === 'requested'}
        enter="transition-opacity duration-200"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="flex flex-col gap-2.5 mt-3">
          <div className="flex gap-2.5">
            <Input
              type="number"
              placeholder="메일로 발송된 인증번호를 입력해 주세요"
              className={'flex-1 w-fit'}
              id="code"
              value={code}
              onChange={handleChangeCode}
              onKeyDown={handleKeyDownEnterCode}
            />
            <OutlinedButton
              className="w-[124px] flex-shrink-0"
              color="primary"
              onClick={handleSumitCode}
              type="button"
              disabled={!code || code.toString().length !== 6 || isVerifying}
            >
              인증 확인
            </OutlinedButton>
          </div>
          <p className="mt-2 font-medium text-xs text-primary">{formatTimemmss(seconds)}</p>
        </div>
      </Transition>
    </>
  );
};

const TimerEnd = ({
  setStatus,
  status,
  setEmailErrorMessage,
  setIsEmailErrored,
}: {
  setStatus: (status: EmailVerifyStatus) => void;
  status: EmailVerifyStatus;
  setEmailErrorMessage: (msg: string | null) => void;
  setIsEmailErrored: (errored: boolean) => void;
}) => {
  useEffect(() => {
    if (status === 'requested') {
      setStatus('not-requested');
      setEmailErrorMessage('인증번호가 만료되었습니다 인증번호를 재발급해주세요 ');
      setIsEmailErrored(true);
      toast.error('인증번호가 만료되었습니다 다시 시도해주세요');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <></>;
};

export default EmailVerify;
